import { useEffect, useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ArrowLeft, CheckCircle, Clock, AlertCircle } from "lucide-react";
import { Link, useLocation } from "wouter";
import { useQuery } from "@tanstack/react-query";

export default function DataDeletion() {
  const lastUpdated = "January 21, 2026";
  const [, setLocation] = useLocation();
  const [confirmationCode, setConfirmationCode] = useState<string | null>(null);
  
  useEffect(() => {
    document.title = "Data Deletion | Eatzy Kudoz";
    
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute("content", "Eatzy Kudoz Data Deletion - Learn how to request deletion of your personal data and what happens when you delete your account.");
    } else {
      const meta = document.createElement('meta');
      meta.name = "description";
      meta.content = "Eatzy Kudoz Data Deletion - Learn how to request deletion of your personal data and what happens when you delete your account.";
      document.head.appendChild(meta);
    }
    
    const params = new URLSearchParams(window.location.search);
    const code = params.get('code');
    if (code) {
      setConfirmationCode(code);
    }
    
    return () => {
      document.title = "Eatzy Kudoz";
    };
  }, []);

  const { data: deletionStatus, isLoading: isLoadingStatus } = useQuery({
    queryKey: ['/api/data-deletion/status', confirmationCode],
    enabled: !!confirmationCode,
  });
  
  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="mb-6">
          <Link href="/login">
            <Button variant="ghost" size="sm" className="gap-2">
              <ArrowLeft className="h-4 w-4" />
              Back
            </Button>
          </Link>
        </div>

        {confirmationCode && (
          <Card className="mb-6 border-primary/20">
            <CardContent className="p-6">
              <h2 className="text-xl font-semibold mb-4">Deletion Request Status</h2>
              {isLoadingStatus ? (
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Clock className="h-5 w-5 animate-pulse" />
                  <span>Checking status...</span>
                </div>
              ) : deletionStatus ? (
                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    {(deletionStatus as any).status === 'completed' && (
                      <>
                        <CheckCircle className="h-5 w-5 text-green-500" />
                        <span className="font-medium text-green-700 dark:text-green-400">Deletion Complete</span>
                      </>
                    )}
                    {(deletionStatus as any).status === 'pending' && (
                      <>
                        <Clock className="h-5 w-5 text-amber-500" />
                        <span className="font-medium text-amber-700 dark:text-amber-400">Deletion Pending</span>
                      </>
                    )}
                    {(deletionStatus as any).status === 'cancelled' && (
                      <>
                        <AlertCircle className="h-5 w-5 text-muted-foreground" />
                        <span className="font-medium">Deletion Cancelled</span>
                      </>
                    )}
                    {(deletionStatus as any).status === 'not_found' && (
                      <>
                        <CheckCircle className="h-5 w-5 text-muted-foreground" />
                        <span className="font-medium">No Active Request</span>
                      </>
                    )}
                  </div>
                  <p className="text-muted-foreground">{(deletionStatus as any).message}</p>
                  <p className="text-sm text-muted-foreground">
                    Confirmation Code: <code className="bg-muted px-2 py-1 rounded">{confirmationCode}</code>
                  </p>
                </div>
              ) : (
                <p className="text-muted-foreground">Unable to retrieve deletion status.</p>
              )}
            </CardContent>
          </Card>
        )}

        <Card>
          <CardContent className="p-6 md:p-8">
            <h1 className="text-3xl font-bold mb-2">Data Deletion</h1>
            <p className="text-muted-foreground mb-8">Last Updated: {lastUpdated}</p>

            <div className="prose prose-sm max-w-none dark:prose-invert space-y-6">
              <section>
                <h2 className="text-xl font-semibold mb-3">Your Right to Delete Your Data</h2>
                <p className="text-muted-foreground leading-relaxed">
                  At Eatzy Kudoz, we respect your privacy and your right to control your personal data. You can request deletion of your account and associated data at any time. This page explains what data we store, how to request deletion, and what happens when you delete your account.
                </p>
              </section>

              <section>
                <h2 className="text-xl font-semibold mb-3">What Data We Store</h2>
                <p className="text-muted-foreground leading-relaxed mb-3">
                  When you use Eatzy Kudoz, we collect and store the following types of data:
                </p>
                <ul className="list-disc pl-6 text-muted-foreground space-y-2">
                  <li><strong>Account Information:</strong> Name, email address, phone number, profile photo, and bio</li>
                  <li><strong>Reviews and Content:</strong> Restaurant reviews, ratings, photos, and comments you've posted</li>
                  <li><strong>Kudoz Rewards:</strong> Your Kudoz balance, earning history, and redemption records</li>
                  <li><strong>Social Connections:</strong> Your followers, following list, and social interactions</li>
                  <li><strong>Order History:</strong> Records of food orders placed through the platform</li>
                  <li><strong>Usage Data:</strong> App activity, preferences, and device information</li>
                </ul>
                <p className="text-muted-foreground leading-relaxed mt-3">
                  For complete details, please see our{' '}
                  <button 
                    className="underline hover:text-foreground"
                    onClick={() => setLocation('/privacy')}
                  >
                    Privacy Policy
                  </button>.
                </p>
              </section>

              <section>
                <h2 className="text-xl font-semibold mb-3">How to Request Account Deletion</h2>
                
                <h3 className="text-lg font-medium mt-4 mb-2">Option 1: In-App Deletion (Recommended)</h3>
                <p className="text-muted-foreground leading-relaxed">
                  The easiest way to delete your account is through the app:
                </p>
                <ol className="list-decimal pl-6 text-muted-foreground space-y-2 mt-2">
                  <li>Log in to your Eatzy Kudoz account</li>
                  <li>Go to <strong>Settings</strong> (tap your profile icon, then Settings)</li>
                  <li>Scroll to the <strong>Account</strong> section</li>
                  <li>Tap <strong>Delete Account</strong></li>
                  <li>Type "DELETE" to confirm your request</li>
                </ol>

                <h3 className="text-lg font-medium mt-4 mb-2">Option 2: Email Request</h3>
                <p className="text-muted-foreground leading-relaxed">
                  You can also request deletion by email:
                </p>
                <div className="mt-3 p-4 bg-muted/50 rounded-lg">
                  <p className="font-medium">Email: privacy@eatzy.com</p>
                  <p className="text-muted-foreground text-sm mt-2">
                    Please include "Account Deletion Request" in the subject line and provide the email address associated with your account.
                  </p>
                </div>

                <h3 className="text-lg font-medium mt-4 mb-2">Option 3: Facebook/Instagram Users</h3>
                <p className="text-muted-foreground leading-relaxed">
                  If you signed up using Facebook or Instagram, you can also request data deletion through your Facebook settings:
                </p>
                <ol className="list-decimal pl-6 text-muted-foreground space-y-2 mt-2">
                  <li>Go to your Facebook Settings & Privacy {">"} Settings</li>
                  <li>Navigate to Apps and Websites</li>
                  <li>Find Eatzy Kudoz and click Remove</li>
                  <li>Check "Delete all posts, photos, and videos" if prompted</li>
                </ol>
              </section>

              <section>
                <h2 className="text-xl font-semibold mb-3">What Happens When You Delete Your Account</h2>
                
                <h3 className="text-lg font-medium mt-4 mb-2">14-Day Grace Period</h3>
                <p className="text-muted-foreground leading-relaxed">
                  When you request account deletion, we schedule it for 14 days in the future. During this grace period:
                </p>
                <ul className="list-disc pl-6 text-muted-foreground space-y-2 mt-2">
                  <li>You can cancel the deletion request at any time by logging in</li>
                  <li>Your account remains accessible but marked for deletion</li>
                  <li>After 14 days, deletion is permanent and cannot be undone</li>
                </ul>

                <h3 className="text-lg font-medium mt-4 mb-2">Data That Will Be Deleted</h3>
                <ul className="list-disc pl-6 text-muted-foreground space-y-2">
                  <li><strong>Personal Information:</strong> Your name, email, phone number, and profile photo will be permanently removed</li>
                  <li><strong>Kudoz Balance:</strong> Any accumulated Kudoz points will be forfeited and cannot be recovered</li>
                  <li><strong>Social Connections:</strong> Your followers and following lists will be cleared</li>
                  <li><strong>Profile Data:</strong> Your bio, preferences, and settings will be deleted</li>
                </ul>

                <h3 className="text-lg font-medium mt-4 mb-2">Data That Will Be Anonymized</h3>
                <ul className="list-disc pl-6 text-muted-foreground space-y-2">
                  <li><strong>Reviews:</strong> Your reviews will remain on the platform but will be attributed to "Deleted User" to maintain the integrity of restaurant ratings</li>
                  <li><strong>Comments:</strong> Comments on reviews will show as from "Deleted User"</li>
                </ul>

                <h3 className="text-lg font-medium mt-4 mb-2">Data We May Retain</h3>
                <p className="text-muted-foreground leading-relaxed">
                  Certain data may be retained for legal, regulatory, or security purposes:
                </p>
                <ul className="list-disc pl-6 text-muted-foreground space-y-2 mt-2">
                  <li><strong>Transaction Records:</strong> Order history may be retained in anonymized form for financial and tax compliance</li>
                  <li><strong>Legal Obligations:</strong> Data required by law enforcement or legal proceedings</li>
                  <li><strong>Security Logs:</strong> Anonymized security and fraud prevention logs</li>
                </ul>
              </section>

              <section>
                <h2 className="text-xl font-semibold mb-3">Processing Time</h2>
                <ul className="list-disc pl-6 text-muted-foreground space-y-2">
                  <li><strong>In-app requests:</strong> Processed automatically after the 14-day grace period</li>
                  <li><strong>Email requests:</strong> We will acknowledge your request within 3 business days and process it within 30 days</li>
                  <li><strong>Facebook/Instagram requests:</strong> Processed within 14 days of receiving the request from Meta</li>
                </ul>
              </section>

              <section>
                <h2 className="text-xl font-semibold mb-3">Questions or Concerns</h2>
                <p className="text-muted-foreground leading-relaxed">
                  If you have any questions about data deletion or need assistance, please contact us:
                </p>
                <div className="mt-4 p-4 bg-muted/50 rounded-lg">
                  <p className="font-medium">Eatzy LLC</p>
                  <p className="text-muted-foreground mt-1">Email: privacy@eatzy.com</p>
                  <p className="text-muted-foreground">Website: www.eatzy.com</p>
                </div>
              </section>
            </div>
          </CardContent>
        </Card>

        <div className="mt-6 flex justify-center gap-4">
          <Link href="/terms">
            <Button variant="ghost" className="text-muted-foreground underline">
              Terms of Service
            </Button>
          </Link>
          <Link href="/privacy">
            <Button variant="ghost" className="text-muted-foreground underline">
              Privacy Policy
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
}
